// Client for the Catalyst gateway.
//
// Two hops (see ai/catalyst_chat_integration_plan.md):
//   1. getToken() — same-origin GET to the Reservoir PHP API /ai/token (session
//      cookie auth). Returns a short-lived bearer JWT; cached until near expiry.
//   2. streamChat() — cross-origin POST to Catalyst /api/chat with the bearer
//      (NO cookies cross-origin). Parses the Vercel AI-SDK v4 data-stream line
//      protocol and invokes callbacks.
//
// No AI SDK dependency: axios can't stream response bodies in the browser, so we
// use fetch + ReadableStream directly and parse the small line protocol here.

import { generateCorrelationId } from '../lib/correlationId';
import { parseDataStreamLine } from '@usalco/catalyst-component-kit';
import { CATALYST_PROFILE_ID } from './constants';

function apiUrl(path) {
  return window.config.API_URL + path;
}

function catalystUrl(path) {
  const base = (window.config.CATALYST_URL || '').replace(/\/$/, '');
  return base + path;
}

// ─── Token cache ───────────────────────────────────────────────────────────
let _token = null;
let _expiresAt = 0; // epoch ms
const REFRESH_SKEW_MS = 30_000;

export function clearToken() {
  _token = null;
  _expiresAt = 0;
}

export async function getToken() {
  if (_token && Date.now() < _expiresAt - REFRESH_SKEW_MS) return _token;
  const res = await fetch(apiUrl('ai/token'), {
    method: 'GET',
    credentials: 'include', // same-origin PHP session cookie
    headers: { 'X-Correlation-ID': generateCorrelationId() },
  });
  if (!res.ok) throw new Error(`Failed to get chat token (${res.status})`);
  const data = await res.json();
  _token = data.token;
  _expiresAt = data.expiresAt || Date.now() + 5 * 60_000;
  return _token;
}

// Create a chat so the turn's messages are persisted server-side and attributed
// to the user (by Azure OID). Returns the new chatId. Best-effort: callers should
// tolerate failure and continue unlogged.
export async function createChat(title) {
  const token = await getToken();
  const res = await fetch(catalystUrl('/api/chats'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + token,
      'X-Correlation-ID': generateCorrelationId(),
    },
    body: JSON.stringify({ title: (title || 'Reservoir chat').slice(0, 200) }),
  });
  if (!res.ok) throw new Error(`Failed to create chat (${res.status})`);
  const data = await res.json();
  return data?.data?.id;
}

// Fetch the profiles this user may use, each with its attached, enabled skills
// ({ id, name, description }). Used to show an active-skills indicator in the chat
// panel. Best-effort: callers tolerate failure (return []) and simply hide the row.
export async function getProfiles() {
  const token = await getToken();
  const res = await fetch(catalystUrl('/api/profiles'), {
    method: 'GET',
    headers: {
      Authorization: 'Bearer ' + token,
      'X-Correlation-ID': generateCorrelationId(),
    },
  });
  if (!res.ok) throw new Error(`Failed to load profiles (${res.status})`);
  const data = await res.json();
  return data?.data || [];
}

// ─── Display Style cache ──────────────────────────────────────────────────────

// Resolved DisplayStyleConfig for the reporting profile, or null if none configured.
// undefined = not yet fetched; null = fetched, none found.
let _displayStyleConfig = undefined;
let _displayStylePromise = null;

// Async: fetches profiles once, extracts the reporting profile's displayStyle config,
// and caches it. Subsequent calls return immediately from cache. Best-effort: callers
// should tolerate null (falls back to palette.js brand constants).
export async function loadDisplayStyleConfig() {
  if (_displayStyleConfig !== undefined) return _displayStyleConfig;
  if (_displayStylePromise) return _displayStylePromise;
  _displayStylePromise = getProfiles()
    .then((profiles) => {
      const profile =
        profiles.find((p) => p.id === CATALYST_PROFILE_ID) || profiles[0] || null;
      _displayStyleConfig = profile?.displayStyle ?? null;
      return _displayStyleConfig;
    })
    .catch(() => { _displayStyleConfig = null; return null; });
  return _displayStylePromise;
}

// Synchronous getter — returns the cached value (null until loadDisplayStyleConfig resolves).
export function getDisplayStyleConfig() {
  return _displayStyleConfig ?? null;
}

// ─── Streaming chat ──────────────────────────────────────────────────────────

// messages: [{ role, content }]. context: optional plain object (page descriptor).
// profileId: optional Catalyst profile override (defaults to CATALYST_PROFILE_ID); the builder
//   passes CATALYST_BUILDER_PROFILE_ID so the model gets the builder-aware system prompt.
// Callbacks: onText(delta), onToolCall({toolCallId,toolName,args}),
//   onToolResult({toolCallId,result}), onFinish({finishReason,usage}), onError(msg).
export async function streamChat(
  { messages, chatId, context, profileId = CATALYST_PROFILE_ID, signal },
  { onText, onToolCall, onToolResult, onFinish, onError } = {},
) {
  const doFetch = async (token) =>
    fetch(catalystUrl('/api/chat'), {
      method: 'POST',
      // Cross-origin to Catalyst: bearer only, do NOT send Reservoir cookies.
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + token,
        'X-Correlation-ID': generateCorrelationId(),
      },
      body: JSON.stringify({ messages, chatId, profileId, context }),
      signal,
    });

  let token = await getToken();
  let res = await doFetch(token);
  if (res.status === 401) {
    // token may have expired mid-cache; refresh once.
    clearToken();
    token = await getToken();
    res = await doFetch(token);
  }
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '');
    throw new Error(`Chat request failed (${res.status}): ${text.slice(0, 200)}`);
  }
  // Soft check: cross-origin responses hide custom headers from JS unless the
  // server sets Access-Control-Expose-Headers, so a missing header is expected and
  // fine — only fail if it's present AND a different version. The body is parsed
  // defensively line-by-line below regardless.
  const streamVer = res.headers.get('X-Vercel-AI-Data-Stream');
  if (streamVer && streamVer !== 'v1') {
    throw new Error(`Unexpected chat stream format: ${streamVer}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Parse the AI-SDK v4 data-stream line protocol via the shared kit (single source
  // of truth for the protocol, identical to Catalyst's own frontend), then dispatch
  // to the callbacks.
  const handleLine = (line) => {
    const ev = parseDataStreamLine(line);
    if (!ev) return;
    switch (ev.type) {
      case 'text':
        onText?.(ev.value);
        break;
      case 'tool-call':
        onToolCall?.({ toolCallId: ev.toolCallId, toolName: ev.toolName, args: ev.args });
        break;
      case 'tool-result':
        onToolResult?.({ toolCallId: ev.toolCallId, result: ev.result });
        break;
      case 'finish':
        onFinish?.({ finishReason: ev.finishReason, usage: ev.usage });
        break;
      case 'error':
        onError?.(ev.error);
        break;
      default:
        break;
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 1);
      handleLine(line);
    }
  }
  if (buffer.trim()) handleLine(buffer.trim());
}
